import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import moment from "moment";
import {
  MessageService,
  FeeType,
  PaymentFrequencyLabel,
  DocumentCategory,
  EnumHelper,
  ProductCode,
  CustomerLoanStatus,
  AccountTransferService,
  SessionStorageKey,
  SessionStorageState,
  SignatureMethod,
  CustomerFacade} from "common";
import { AcceptLoanAgreementResponse } from "../loc-agreement.data";
import { LocAgreementService } from "../loc-agreement.service";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AgreementDto, OfferInfo, OfferBundleInfo } from "../../agreement/agreement.data";
import { BankAccountService } from "../../bank-account/bank-account.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LoanData } from "../../loan/domain/models/loan.model";
import { PostSignNavigationService } from "../post-sign-navigation.service";
import { DocuSignSignStepSummary } from "../docusign-sign-step/docusign-sign-step.component";
import { SigningSubject, SigningSubjectKind } from "../domain/models/docusign-signing.model";
import { AgreementDisclosureProgress } from "../domain/models/agreement-disclosure.model";
import { AgreementDocumentComponent } from "../agreement-document/agreement-document.component";
import { SsoAccountData, SsoAccountFacade } from "common";

@UntilDestroy()
@Component({
    selector: "ifp-loc-agreement-form",
    templateUrl: "./loc-agreement-form.component.html",
    styleUrls: ["./loc-agreement-form.component.scss"],
    standalone: false
})
export class LocAgreementFormComponent implements OnInit {
  /** How long the Accept button bumps; matches the animation in the stylesheet. */
  static readonly AcceptBumpDuration = 1800;

  data: LocAgreementFormComponentData;
  productCodes = ProductCode;
  offerBundleId?: number;
  selectedOfferId?: number;
  templateId?: number;
  locOfferDetails = {
    creditLimit: null,
    repaymentTerm: null,
  };
  termLoanOfferDetails = {
    loanAmount: null,
    originationFeeAmount: null,
    disbursedAmount: null,
    factorRate: null,
    totalInterest: null,
    totalRepaymentAmount: null,
    paymentAmount: null,
    totalNumberOfPayments: null,
  };
  currentOffer: OfferInfo;
  agreementInfo: any;
  locAgreement: AgreementDto;
  activationMessage: string;
  dba: string;
  bankIntegrationRequired: boolean;
  isDisbursementBankEligible: boolean = false;

  /** Which signing surface to show, as resolved server-side and published on the agreement state. */
  isDocuSignSigning: boolean = false;

  /** The figures the DocuSign sign step's summary card presents. */
  docuSignSummary: DocuSignSignStepSummary;

  /** What the DocuSign sign step signs: this loan. */
  signingSubject: SigningSubject;

  /** Disclosures on the agreement still to be accepted; signing waits for none. */
  hasPendingDisclosures: boolean = false;

  /** Briefly set once the last disclosure is accepted, so the Accept button stands out as the next click. */
  acceptButtonBumped = false;

  /** No disclosure is accepted against an ineligible disbursement account, as before. */
  readonly disclosureAcceptGuard = (): boolean => this.checkDisbursementAccount();

  /** Set once the agreement has reported its disclosures; until then nothing is known to be accepted. */
  private disclosureProgressKnown = false;

  private acceptBumpTimer: ReturnType<typeof setTimeout>;

  @ViewChild("agreementDocument")
  agreementDocument: AgreementDocumentComponent;

  @ViewChild("acceptButton", { read: ElementRef })
  acceptButton: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locAgreementService: LocAgreementService,
    private messageService: MessageService,
    private accountTransferService: AccountTransferService,
    private customerFacade: CustomerFacade,
    private bankAccountService: BankAccountService,
    private snackBar: MatSnackBar,
    private ssoAccountFacade: SsoAccountFacade,
    private postSignNavigation: PostSignNavigationService
  ) {
    SessionStorageState.set(SessionStorageKey.AccountSetupViewed, true);
    SessionStorageState.set(SessionStorageKey.AccountAgreementViewed, true);
    if ((window as any)?.flutter_inappwebview) {
      this.isInWebView = true;
    }
  }

  isInWebView: boolean = false;

  get FeeType() {
    return FeeType;
  }

  get categoryName() {
    return EnumHelper.getNameFromValue(
      DocumentCategory,
      this.locAgreement?.category
    );
  }

  download() {
    this.locAgreementService.download(
      this.data.loan.id,
      this.offerBundleId,
      this.selectedOfferId
    );
  }

  reloadComponent() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate([`account/${this.data.loan.id}/setup/sign-agreement`]);
  }

  ngOnInit() {
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: LocAgreementFormComponentData) =>
        this.onDataInit(data)
      );

    if (!this.isDocuSignSigning) {
      this.locAgreementService
        .getAgreement(this.data.loan.id, this.offerBundleId, this.selectedOfferId)
        .pipe(untilDestroyed(this))
        .subscribe((data) => {
          this.templateId = data.templateId;
          this.locAgreement = data;
        });
    }

    this.prepareAgreementData();

    const pendingloan = this.data?.currentSsoAccount?.loans?.find(
      loan => loan?.loanId === this.data?.loan?.id
    );
    this.isDisbursementBankEligible = pendingloan?.agreementState?.errors?.isDisbursementBankEligible ?? false;

    this.checkDisbursementAccount();

    this.bankIntegrationRequired = false;
    if (this.data && this.data.loan && this.data.loan.entityId) {
      this.bankAccountService
        .getIsBankLinkReminderRequired(this.data.loan.entityId)
        .subscribe(
          (isBankLinkReminderRequired) =>
            (this.bankIntegrationRequired = isBankLinkReminderRequired)
        );
    }
  }

  private onDataInit(data: LocAgreementFormComponentData): void {
    this.data = data;

    this.offerBundleId =  this.data?.offerBundlesInfo?.offerBundleId;
    this.selectedOfferId = this.data?.offerBundlesInfo?.selectedOffer?.id;
    this.isDocuSignSigning = this.resolveSignatureMethod() === SignatureMethod.DocuSign;
    this.signingSubject = { kind: SigningSubjectKind.Loan, id: this.data.loan.id };

    if (moment(data.loan.createdOn).isSame(moment().startOf("day"), "d")) {
      // eslint-disable-next-line max-len
      this.activationMessage =
        "You have two (2) days to activate your loan and draw funds. Funds requested after two days are subject to an additional underwriting review.";
    } else if (
      moment(data.loan.createdOn).isSame(
        moment().subtract(1, "days").startOf("day"),
        "d"
      )
    ) {
      // eslint-disable-next-line max-len
      this.activationMessage =
        "You have until today day to activate your loan and draw funds. Funds requested after one (1) day are subject to an additional underwriting review.";
    } else if (
      moment(data.loan.createdOn).isSameOrBefore(
        moment().subtract(2, "days").startOf("day"),
        "d"
      )
    ) {
      // eslint-disable-next-line max-len
      this.activationMessage =
        "It has been two (2) or more days after your loan was created. Any funds requested to activate your loan will be subject to an additional underwriting review.";
    }
  }

  /** The signing method for what is actually being signed on this screen. */
  private resolveSignatureMethod(): SignatureMethod {
    const loanMeta = this.data?.currentSsoAccount?.loans?.find(
      (loan) => loan?.loanId === this.data?.loan?.id
    );

    return (
      (this.offerBundleId ? loanMeta?.renewalApplication?.signatureMethod : undefined) ??
      loanMeta?.agreementState?.signatureMethod ??
      SignatureMethod.Internal
    );
  }

  private prepareAgreementData(): void {
    const pendingOffers =
      this.data.loan.customerFacingStatus === CustomerLoanStatus.PendingRenewal &&
      this.data.offerBundlesInfo &&
      this.data.offerBundlesInfo.selectedOffer;

    this.agreementInfo = pendingOffers
      ? this.data.offerBundlesInfo.selectedOffer
      : this.data.loan;
    this.currentOffer = pendingOffers
      ? this.data.offerBundlesInfo.selectedOffer
      : this.data.loan.offer;

    if (this.agreementInfo.productCode === this.productCodes.Term)
      this.getTermLoanCalculations();
    else if (
      this.agreementInfo.productCode === this.productCodes.LOC ||
      this.agreementInfo.productCode === this.productCodes.Law
    )
      this.getLineOfCreditCalculations();

    this.docuSignSummary = {
      entityName: this.data.loan.entityName,
      loanNumber: this.data.loan.loanNumber,
      amount: this.currentOffer?.amount,
      amountLabel:
        this.agreementInfo.productCode === this.productCodes.Term ? "Loan amount" : "Credit limit",
      repaymentTerm: this.currentOffer?.repaymentTerm,
      paymentFrequencyLabel: this.loanOfferPaymentFrequencyLabel,
      stateName: this.data.loan.entityAddress?.state,
      productName: this.data.loan.productName,
    };
  }

  print() {
    const innerContents = document.getElementById(
      `account-setup-loc-agreement`
    ).innerHTML;
    const popupWinindow = window.open(
      "",
      "_blank",
      "width=600,height=700,scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no"
    );
    popupWinindow.document.open();
    popupWinindow.document.write(
      `<html><head><link rel="stylesheet" type="text/css" href="style.css" /></head><body onload="window.print()">${innerContents}</html>`
    );
    popupWinindow.document.close();
  }

  get loanOfferPaymentFrequencyLabel() {
    return PaymentFrequencyLabel.get(this.currentOffer.paymentFrequency);
  }

  /** Brings the first disclosure still to be accepted into view. */
  start(): void {
    this.agreementDocument?.scrollToFirstPending();
  }

  review(): void {
    if (!this.disclosureProgressKnown) return;

    if (!this.checkDisbursementAccount()) return;

    if (this.hasPendingDisclosures) {
      this.agreementDocument?.scrollToFirstPending();
      this.snackBar.open("Please accept every disclosure before accepting the agreement.", "", { duration: 3000 });
      return;
    }

    this.submit();
  }

  onDisclosureProgress(progress: AgreementDisclosureProgress): void {
    this.disclosureProgressKnown = true;
    this.hasPendingDisclosures = progress.pendingCount > 0;
  }

  /** The last disclosure is accepted: Accept is what to click next. */
  onDisclosuresCompleted(): void {
    this.acceptButton?.nativeElement.scrollIntoView({ block: "center", behavior: "smooth" });

    clearTimeout(this.acceptBumpTimer);
    this.acceptButtonBumped = true;
    this.acceptBumpTimer = setTimeout(() => (this.acceptButtonBumped = false), LocAgreementFormComponent.AcceptBumpDuration);
  }

  private checkDisbursementAccount(): boolean {
    if (!this.isDisbursementBankEligible) {
      this.snackBar.open(
        "Your disbursement bank account is invalid. Please contact your business advisor.",
        "",
        { duration: 5000 }
      );
      return false;
    }
    return true;
  }

  submit() {
    if (this.submitSub) return;

    this.customerFacade.clearCustomerCache();

    this.submitSub = this.locAgreementService
      .submit(this.data.loan.id, {
        accept: true,
        templateId: this.templateId,
        offerBundleId: this.offerBundleId,
        selectedOfferId: this.selectedOfferId,
      })
      .subscribe(this.submitSuccess.bind(this), this.submitError.bind(this));
  }

  submitError(error: any) {
    this.submitSubClear();
    this.messageService.error(error);
  }

  submitSub: Subscription;

  submitSubClear() {
    if (this.submitSub) this.submitSub.unsubscribe();
    this.submitSub = null;
  }

  submitSuccess(response: AcceptLoanAgreementResponse) {
    this.submitSubClear();

    this.ssoAccountFacade.clearSsoAccountCache();

    if (!response.isFinal) {
      this.reloadComponent();
      return;
    }

    if (!response.loanId) {
      this.messageService.info(
        `Please make sure you checked 'By clicking this checkbox you agree to the terms and conditions set forth in the agreement above.'`
      );
      return;
    }

    // Shared with the DocuSign return page so the two signing paths land in the same place.
    this.postSignNavigation
      .navigateAfterSigning$({
        signedLoanId: response.loanId,
        sourceLoanId: this.data.loan.id,
        entityId: this.data.loan.entityId,
        offerBundleId: this.offerBundleId,
        bankIntegrationRequired: this.bankIntegrationRequired,
        disbursedAmount: this.termLoanOfferDetails.disbursedAmount,
      })
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  getLineOfCreditCalculations() {
    const currentOffer = { ...this.currentOffer };
    this.locOfferDetails.creditLimit = currentOffer.amount;
    this.locOfferDetails.repaymentTerm = currentOffer.repaymentTerm;
  }

  getTermLoanCalculations() {
    const currentOffer = { ...this.currentOffer };

    this.termLoanOfferDetails.loanAmount = currentOffer.amount;

    this.termLoanOfferDetails.originationFeeAmount =
      this.accountTransferService.calculateFeeAmount(
        currentOffer.amount,
        currentOffer.originationFee,
        currentOffer.originationFeeType
      );

    this.termLoanOfferDetails.disbursedAmount =
      this.accountTransferService.calculateDisbursedAmount(
        currentOffer.amount,
        this.termLoanOfferDetails.originationFeeAmount
      );

    this.termLoanOfferDetails.factorRate =
      currentOffer.interestRate / 100.0 + 1;

    this.termLoanOfferDetails.totalInterest =
      this.accountTransferService.calculateTotalInterest(
        currentOffer.amount,
        currentOffer.interestRate
      );

    this.termLoanOfferDetails.totalRepaymentAmount =
      currentOffer.amount + this.termLoanOfferDetails.totalInterest;

    this.termLoanOfferDetails.totalNumberOfPayments =
      this.accountTransferService.getTotalNumberOfPayments(
        currentOffer.paymentFrequency,
        currentOffer.repaymentTerm
      );

    this.termLoanOfferDetails.paymentAmount =
      this.accountTransferService.calculatePayments(
        currentOffer.amount,
        this.termLoanOfferDetails.totalInterest,
        currentOffer.paymentFrequency,
        currentOffer.repaymentTerm
      );
  }
}

export interface LocAgreementFormComponentData {
  currentSsoAccount: SsoAccountData;
  loan: LoanData;
  offerBundlesInfo: OfferBundleInfo;
}
